import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, lastValueFrom, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { sseStream as openSseStream } from '../common/sse';
import {
  AppService, Profile, HumanReviewQueue, UploadResult,
  ImageFolderList, ImageSummaryData, ImageGifList, StreamStatus, ValidationRow,
  ReviewVariableData, ExecutionPreviewData, ExecutionPreviewQuery, ExecutionSummary, TransformChatMessage, TransformSuggestion,
  TargetReloadData, TransformReloadData, TransformationOption, SplitVariableResult,
  PipelineStats, ValidationSummary, UploadDirListing, UploadFilePreview,
  PagedResult, AuditVariable, ProfileVariableRow, Session, PlanQueryOpts, ProfileQueryOpts,
  ProfileVariableEditDefaults, ProfileVariableEditPatch,
  ColumnValuesQuery, ColumnValuesData, ExecutionFailures,
} from './app.service';

const SESSION_KEY = 'aha_session_id';

@Injectable()
export class RealService extends AppService {
  private readonly http = inject(HttpClient);
  private readonly api = environment.apiUrl;
  private readonly savedNames: Record<number, string> = {};
  private runName = '';

  constructor() {
    super();
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) this.sessionId = stored;
    }
  }

  override setRunName(name: string): void { this.runName = name; }

  override setSession(id: string): void {
    this.sessionId = id;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SESSION_KEY, id);
    }
  }

  private get resolvedSessionId(): string {
    if (!this.sessionId && typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) this.sessionId = stored;
    }
    return this.sessionId;
  }

  // Shared with the omics real service — see ./sse.ts. Kept as a thin private
  // delegate so every call site below reads unchanged.
  private sseStream(url: string): Observable<StreamStatus> {
    return openSseStream(url);
  }

  // ── Upload Module ─────────────────────────────────────────
  // POST /aha/v1/upload/file  (multipart/form-data)

  uploadFile(file: File, card: string, slot: number, serverPath?: string): Promise<UploadResult> {
    // card 'a' → backend slots 0,1 ; card 'b' → backend slots 2,3
    const backendSlot = card === 'a' ? slot : slot + 2;
    return lastValueFrom(
      this.http.post<{ ok: boolean; saved_name: string }>(`${this.api}/upload/file`, {
        // Must be the path relative to the files root — `file.name` is only the
        // basename, which the backend can't resolve for anything in a subfolder.
        path: serverPath ?? file.name,
        slot: backendSlot,
      }).pipe(map(res => {
        this.savedNames[backendSlot] = res.saved_name;
        return { ok: res.ok, savedName: res.saved_name };
      }))
    );
  }

  // GET /aha/v1/upload/files?path=<dir>
  // One folder's immediate children — the endpoint is lazy by design: it reads a
  // single directory and opens no files, so the picker calls it once per folder
  // the user expands instead of paying for a full recursive walk up front.
  browseUploadDir(path: string): Observable<UploadDirListing> {
    return this.http.get<UploadDirListing>(`${this.api}/upload/files`, {
      params: path ? { path } : {},
    });
  }

  // GET /aha/v1/upload/file-preview?name=<path>
  // The listing's preview is type-only; this is the call that actually opens the
  // file, so it's made for the one file the user selects.
  getUploadFilePreview(path: string): Observable<UploadFilePreview> {
    return this.http.get<UploadFilePreview>(`${this.api}/upload/file-preview`, {
      params: { name: path },
    });
  }

  // GET /aha/v1/upload/file-content?name=<path>
  getUploadFilePreviewUrl(path: string): string {
    return `${this.api}/upload/file-content?name=${encodeURIComponent(path)}`;
  }

  getPreviewLoadDelay(): number { return 0; }

  // POST /aha/v1/sessions → creates session_id, then POST /aha/v1/profiling/start, then SSE
  watchUploadStatus(): Observable<StreamStatus> {
    const sid = this.resolvedSessionId;

    // savedNames lives in memory — restore from localStorage after a page refresh
    if (!this.savedNames[0] && typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('aha_session_files');
      if (stored) {
        try {
          const f = JSON.parse(stored);
          if (f.unharmonized_dataset?.name)  this.savedNames[0] = f.unharmonized_dataset.name;
          if (f.standard_dataset?.name)      this.savedNames[1] = f.standard_dataset.name;
          if (f.unharmonized_codebook?.name) this.savedNames[2] = f.unharmonized_codebook.name;
          if (f.standard_codebook?.name)     this.savedNames[3] = f.standard_codebook.name;
        } catch { /* ignore malformed */ }
      }
    }

    if (sid) {
      return new Observable(observer => {
        this.http.post<{ ok: boolean }>(`${this.api}/profiling/start`, { session_id: sid })
          .subscribe({
            next: () => this.sseStream(`${this.api}/profiling/status?session_id=${sid}`).subscribe(observer),
            error: err => observer.error(err),
          });
      });
    }
    const sessionBody = {
      unharmonized_dataset_filename: this.savedNames[0],
      standard_dataset_filename: this.savedNames[1],
      unharmonized_codebook_filename: this.savedNames[2],
      standard_codebook_filename: this.savedNames[3],
      name: this.runName,
    };
    return new Observable(observer => {
      this.http.post<{ ok: boolean; session_id: string }>(`${this.api}/sessions`, sessionBody)
        .subscribe({
          next: sessionRes => {
            this.setSession(sessionRes.session_id);
            this.http.post<{ ok: boolean; session_id: string }>(
              `${this.api}/profiling/start`,
              { session_id: sessionRes.session_id }
            ).subscribe({
              next: () => {
                this.sseStream(`${this.api}/profiling/status?session_id=${sessionRes.session_id}`)
                  .subscribe(observer);
              },
              error: err => observer.error(err),
            });
          },
          error: err => observer.error(err),
        });
    });
  }

  reconnectUploadStatus(): Observable<StreamStatus> {
    return this.sseStream(`${this.api}/profiling/status?session_id=${this.resolvedSessionId}`);
  }

  // ── Data Profiling Module ─────────────────────────────────
  // POST /aha/v1/profiling/start  →  stores session_id
  // GET  /aha/v1/profiling/status (SSE)
  // GET  /aha/v1/profiling/profiles

  getProfiles(): Observable<Record<string, Profile>> {
    return this.http.get<Record<string, Profile>>(`${this.api}/profiling/profiles`, {
      params: { session_id: this.resolvedSessionId },
    });
  }

  reconnectProfilingStatus(): Observable<StreamStatus> {
    return this.sseStream(`${this.api}/plan/status?session_id=${this.resolvedSessionId}`);
  }

  // POST /aha/v1/plan/start → then SSE /plan/status
  watchProfilingStatus(): Observable<StreamStatus> {
    return new Observable(observer => {
      this.http.post<{ ok: boolean; session_id: string }>(`${this.api}/plan/start`, { session_id: this.resolvedSessionId })
        .subscribe({
          next: () => this.sseStream(`${this.api}/plan/status?session_id=${this.resolvedSessionId}`).subscribe(observer),
          error: err => observer.error(err),
        });
    });
  }

  // ── Strategy & Planning Module ────────────────────────────
  // POST /aha/v1/plan/start  (session_id in body)
  // GET  /aha/v1/plan/status (SSE)
  // GET  /aha/v1/plan

  reconnectPlanningStatus(): Observable<StreamStatus> {
    return this.sseStream(`${this.api}/execution/status?session_id=${this.resolvedSessionId}`);
  }

  // POST /aha/v1/execution/start → then SSE /execution/status
  watchPlanningStatus(): Observable<StreamStatus> {
    return new Observable(observer => {
      this.http.post<{ ok: boolean; session_id: string }>(`${this.api}/execution/start`, { session_id: this.resolvedSessionId })
        .subscribe({
          next: () => this.sseStream(`${this.api}/execution/status?session_id=${this.resolvedSessionId}`).subscribe(observer),
          error: err => observer.error(err),
        });
    });
  }

  getHumanReviewQueue(threshold: number | null, unreviewed = false): Observable<HumanReviewQueue> {
    if (unreviewed) {
      return this.http.get<HumanReviewQueue>(
        `${this.api}/plan/human-review?session_id=${this.resolvedSessionId}&unreviewed`
      );
    }
    if (threshold === null) {
      return this.http.get<HumanReviewQueue>(
        `${this.api}/plan/human-review?session_id=${this.resolvedSessionId}&review_all`
      );
    }
    return this.http.get<HumanReviewQueue>(`${this.api}/plan/human-review`, {
      params: { session_id: this.resolvedSessionId, threshold: String(threshold) },
    });
  }

  checkAllNotMapped(): Observable<{ all_not_mapped: boolean }> {
    return this.http.get<{ all_not_mapped: boolean }>(`${this.api}/plan/all-not-mapped`, {
      params: { session_id: this.resolvedSessionId },
    });
  }

  saveSteps(_decisionId: string, _steps: unknown[]): void {
    throw new Error('RealService.saveSteps not implemented');
  }

  // GET /aha/v1/plan/variables/{decision_id}/review?session_id
  getReviewData(decisionId: string): Observable<ReviewVariableData> {
    return this.http.get<ReviewVariableData>(
      `${this.api}/plan/variables/${encodeURIComponent(decisionId)}/review`,
      { params: { session_id: this.resolvedSessionId } },
    );
  }

  // POST /aha/v1/plan/variables/{decision_id}/targets/start
  startDecisionChange(decisionId: string, decision: string, mappingFrom?: string[], selectedTargetId?: string): Observable<{ ok: boolean }> {
    const body: Record<string, unknown> = { session_id: this.resolvedSessionId, decision };
    if (mappingFrom?.length) body['mapping_from'] = mappingFrom;
    if (selectedTargetId) body['selected_target_id'] = selectedTargetId;
    return this.http.post<{ ok: boolean }>(
      `${this.api}/plan/variables/${encodeURIComponent(decisionId)}/targets/start`,
      body,
    );
  }

  // GET /aha/v1/plan/variables/{decision_id}/targets/status?session_id (SSE)
  watchDecisionChangeStatus(decisionId: string, _decision: string): Observable<StreamStatus> {
    return this.sseStream(
      `${this.api}/plan/variables/${encodeURIComponent(decisionId)}/targets/status?session_id=${this.resolvedSessionId}`
    );
  }

  // GET /aha/v1/plan/variables/{decision_id}/targets?session_id&decision
  getDecisionTargets(decisionId: string, decision: string): Observable<TargetReloadData> {
    return this.http.get<TargetReloadData>(
      `${this.api}/plan/variables/${encodeURIComponent(decisionId)}/targets`,
      { params: { session_id: this.resolvedSessionId, decision } },
    );
  }

  // POST /aha/v1/plan/variables/{decision_id}/transforms/start
  startTransformReload(decisionId: string, decision: string, targetId: string, mappingFrom?: string[]): Observable<{ ok: boolean }> {
    const body: Record<string, unknown> = { session_id: this.resolvedSessionId, decision, target_id: targetId };
    if (mappingFrom?.length) body['mapping_from'] = mappingFrom;
    return this.http.post<{ ok: boolean }>(
      `${this.api}/plan/variables/${encodeURIComponent(decisionId)}/transforms/start`,
      body,
    );
  }

  // GET /aha/v1/plan/variables/{decision_id}/transforms/status?session_id (SSE)
  watchTransformReloadStatus(decisionId: string, _decision: string, _targetId: string): Observable<StreamStatus> {
    return this.sseStream(
      `${this.api}/plan/variables/${encodeURIComponent(decisionId)}/transforms/status?session_id=${this.resolvedSessionId}`
    );
  }

  // GET /aha/v1/plan/variables/{decision_id}/transforms?session_id&target
  getTargetTransforms(decisionId: string, targetId: string): Observable<TransformReloadData> {
    return this.http.get<TransformReloadData>(
      `${this.api}/plan/variables/${encodeURIComponent(decisionId)}/transforms`,
      { params: { session_id: this.resolvedSessionId, target: targetId } },
    );
  }

  // PATCH /aha/v1/plan/variables/{decision_id}/review
  markVariableReviewed(
    decisionId: string,
    decision: string,
    targetId: string | null,
    selectedTransformation: TransformationOption | null,
    mappingFrom?: string[],
  ): Observable<void> {
    const body: Record<string, unknown> = { session_id: this.resolvedSessionId, decision };
    if (targetId) body['target_id'] = targetId;
    if (selectedTransformation) body['selected_transformation'] = selectedTransformation;
    if (mappingFrom?.length) body['mapping_from'] = mappingFrom;
    return this.http.patch<void>(
      `${this.api}/plan/variables/${encodeURIComponent(decisionId)}/review`,
      body,
    );
  }

  // PATCH /aha/v1/plan/variables/{decision_id}/split
  splitVariable(decisionId: string): Observable<SplitVariableResult> {
    return this.http.patch<SplitVariableResult>(
      `${this.api}/plan/variables/${encodeURIComponent(decisionId)}/split`,
      { session_id: this.resolvedSessionId },
    );
  }

  // POST /aha/v1/plan/variables/{decision_id}/transform/suggest
  suggestTransformation(
    decisionId: string,
    decision: string,
    targetId: string,
    selectedTransformation: TransformationOption | null,
    userMessage: string,
    history: TransformChatMessage[],
  ): Observable<TransformSuggestion> {
    return this.http.post<TransformSuggestion>(
      `${this.api}/plan/variables/${encodeURIComponent(decisionId)}/transform/suggest`,
      {
        session_id: this.resolvedSessionId,
        decision,
        target_id: targetId,
        selected_transformation: selectedTransformation,
        message: userMessage,
        history,
      },
    );
  }

  // POST /plan/variables/{id}/transforms — create a custom transformation.
  createTransform(decisionId: string, targetId: string, code: Record<string, unknown>[]): Observable<TransformationOption> {
    return this.http.post<{ transformation: TransformationOption }>(
      `${this.api}/plan/variables/${encodeURIComponent(decisionId)}/transforms`,
      { session_id: this.resolvedSessionId, target_id: targetId, code },
    ).pipe(map(r => r.transformation));
  }

  // PATCH /plan/variables/{id}/transforms/{transformation_id} — edit a transformation.
  editTransform(decisionId: string, transformationId: string, code: Record<string, unknown>[]): Observable<TransformationOption> {
    return this.http.patch<{ transformation: TransformationOption }>(
      `${this.api}/plan/variables/${encodeURIComponent(decisionId)}/transforms/${encodeURIComponent(transformationId)}`,
      { session_id: this.resolvedSessionId, code },
    ).pipe(map(r => r.transformation));
  }

  // ── Execution Module ──────────────────────────────────────
  // POST /aha/v1/execution/start
  // GET  /aha/v1/execution/status (SSE)
  // GET  /aha/v1/execution/preview

  startExecution(): Observable<{ ok: boolean; session_id: string }> {
    return this.http.post<{ ok: boolean; session_id: string }>(
      `${this.api}/execution/start`,
      { session_id: this.resolvedSessionId },
    );
  }

  reconnectExecutionStatus(): Observable<StreamStatus> {
    return this.sseStream(`${this.api}/validation/status?session_id=${this.resolvedSessionId}`);
  }

  // POST /aha/v1/validation/start → then SSE /validation/status
  watchExecutionStatus(): Observable<StreamStatus> {
    return new Observable(observer => {
      this.http.post<{ ok: boolean; session_id: string }>(`${this.api}/validation/start`, { session_id: this.resolvedSessionId })
        .subscribe({
          next: () => this.sseStream(`${this.api}/validation/status?session_id=${this.resolvedSessionId}`).subscribe(observer),
          error: err => observer.error(err),
        });
    });
  }

  // GET /aha/v1/execution/summary?session_id
  getExecutionSummary(): Observable<ExecutionSummary> {
    return this.http.get<ExecutionSummary>(`${this.api}/execution/summary`, {
      params: { session_id: this.resolvedSessionId },
    });
  }

  // GET /aha/v1/execution/failures?session_id
  // The summary's complement: it says how many of a decision ran, this says which ones did
  // not. No filter — the whole list is small, and each row carries its own decision, so the
  // screen can move between them without another round trip.
  getExecutionFailures(): Observable<ExecutionFailures> {
    return this.http.get<ExecutionFailures>(`${this.api}/execution/failures`, {
      params: { session_id: this.resolvedSessionId },
    });
  }

  // GET /aha/v1/execution/preview?session_id&page&page_size&col_limit
  //   [&decision][&search][&column_filters]
  // Every narrowing is the server's: the preview is paged there, so a filter applied in the
  // browser could only reach the rows already fetched.
  getExecutionPreview(q: ExecutionPreviewQuery): Observable<ExecutionPreviewData> {
    const params: Record<string, string> = {
      session_id: this.resolvedSessionId,
      page: String(q.page),
      page_size: String(q.page_size),
    };
    if (q.col_limit) params['col_limit'] = String(q.col_limit);
    if (q.decision) params['decision'] = q.decision;
    if (q.search?.trim()) params['search'] = q.search.trim();
    // Omitted means bidirectional, which is the server's own default — so it is left out
    // rather than sent as a word meaning "both".
    if (q.search_in) params['search_in'] = q.search_in;
    if (q.column_filters && Object.keys(q.column_filters).length) {
      params['column_filters'] = JSON.stringify(q.column_filters);
    }
    if (q.filter_values_limit) params['filter_values_limit'] = String(q.filter_values_limit);
    return this.http.get<ExecutionPreviewData>(`${this.api}/execution/preview`, { params });
  }

  // GET /aha/v1/execution/column-values
  getColumnValues(q: ColumnValuesQuery): Observable<ColumnValuesData> {
    const params: Record<string, string> = {
      session_id: this.resolvedSessionId,
      side: q.side,
      column: q.column,
    };
    if (q.column_filters && Object.keys(q.column_filters).length) {
      params['column_filters'] = JSON.stringify(q.column_filters);
    }
    return this.http.get<ColumnValuesData>(`${this.api}/execution/column-values`, { params });
  }


  getPlanVariables(page: number, pageSize: number, opts?: PlanQueryOpts): Observable<PagedResult<AuditVariable>> {
    const params: Record<string, string> = { session_id: this.resolvedSessionId, page: String(page), page_size: String(pageSize) };
    if (opts?.replanFilter) params['replan_filter'] = 'true';
    if (opts?.filters?.length) params['filter'] = opts.filters.join(',');
    if (opts?.threshold !== undefined) params['threshold'] = String(opts.threshold);
    if (opts?.sortBy) params['sort_by'] = opts.sortBy;
    return this.http.get<PagedResult<AuditVariable>>(`${this.api}/plan/variables`, { params });
  }

  searchPlanVariables(q: string, page: number, pageSize: number, opts?: PlanQueryOpts): Observable<PagedResult<AuditVariable>> {
    const params: Record<string, string> = { session_id: this.resolvedSessionId, q, page: String(page), page_size: String(pageSize) };
    if (opts?.replanFilter) params['replan_filter'] = 'true';
    if (opts?.filters?.length) params['filter'] = opts.filters.join(',');
    if (opts?.threshold !== undefined) params['threshold'] = String(opts.threshold);
    if (opts?.sortBy) params['sort_by'] = opts.sortBy;
    return this.http.get<PagedResult<AuditVariable>>(`${this.api}/plan/variables/search`, { params });
  }

  getProfileVariables(dataset: string, page: number, pageSize: number, opts?: ProfileQueryOpts): Observable<PagedResult<ProfileVariableRow>> {
    const params: Record<string, string> = { session_id: this.resolvedSessionId, page: String(page), page_size: String(pageSize) };
    if (opts?.sortBy) params['sort_by'] = opts.sortBy;
    if (opts?.filters?.length) params['filter'] = opts.filters.join(',');
    if (opts?.priorityIds?.length) params['priority_ids'] = opts.priorityIds.join(',');
    return this.http.get<PagedResult<ProfileVariableRow>>(
      `${this.api}/profiling/profiles/${encodeURIComponent(dataset)}/variables`,
      { params },
    );
  }

  searchProfileVariables(dataset: string, q: string, page: number, pageSize: number, opts?: ProfileQueryOpts): Observable<PagedResult<ProfileVariableRow>> {
    const params: Record<string, string> = { session_id: this.resolvedSessionId, q, page: String(page), page_size: String(pageSize) };
    if (opts?.sortBy) params['sort_by'] = opts.sortBy;
    if (opts?.filters?.length) params['filter'] = opts.filters.join(',');
    if (opts?.priorityIds?.length) params['priority_ids'] = opts.priorityIds.join(',');
    return this.http.get<PagedResult<ProfileVariableRow>>(
      `${this.api}/profiling/profiles/${encodeURIComponent(dataset)}/variables/search`,
      { params },
    );
  }

  // Wired to the endpoints documented in API_ENDPOINTS.md ("Get Profile Variable Edit
  // Suggestions" / "Update Profile Variable" / "Delete Profile Variable").

  // GET /aha/v1/profiling/profiles/{dataset}/variables/{variable_name}/edit
  getProfileVariableEditSuggestions(dataset: string, variableName: string): Observable<ProfileVariableEditDefaults> {
    return this.http.get<ProfileVariableEditDefaults>(
      `${this.api}/profiling/profiles/${encodeURIComponent(dataset)}/variables/${encodeURIComponent(variableName)}/edit`,
      { params: { session_id: this.resolvedSessionId } },
    );
  }

  // PATCH /aha/v1/profiling/profiles/{dataset}/variables/{variable_name}
  updateProfileVariable(dataset: string, variableName: string, patch: ProfileVariableEditPatch): Observable<void> {
    return this.http.patch<void>(
      `${this.api}/profiling/profiles/${encodeURIComponent(dataset)}/variables/${encodeURIComponent(variableName)}`,
      { variable_name: variableName, ...patch },
      { params: { session_id: this.resolvedSessionId } },
    );
  }

  // DELETE /aha/v1/profiling/profiles/{dataset}/variables/{variable_name}
  deleteProfileVariable(dataset: string, variableName: string): Observable<void> {
    return this.http.delete<void>(
      `${this.api}/profiling/profiles/${encodeURIComponent(dataset)}/variables/${encodeURIComponent(variableName)}`,
      { params: { session_id: this.resolvedSessionId } },
    );
  }

  // ── Validation Module ─────────────────────────────────────
  // GET  /aha/v1/validation/report?session_id
  // POST /aha/v1/validation/replan

  getValidationResults(page: number, pageSize: number, sortBy?: string): Observable<PagedResult<ValidationRow>> {
    const params: Record<string, string> = { session_id: this.resolvedSessionId, page: String(page), page_size: String(pageSize) };
    if (sortBy) params['sort_by'] = sortBy;
    return this.http.get<PagedResult<ValidationRow>>(`${this.api}/validation/report`, { params });
  }

  searchValidationResults(q: string, page: number, pageSize: number, sortBy?: string): Observable<PagedResult<ValidationRow>> {
    const params: Record<string, string> = { session_id: this.resolvedSessionId, q, page: String(page), page_size: String(pageSize) };
    if (sortBy) params['sort_by'] = sortBy;
    return this.http.get<PagedResult<ValidationRow>>(`${this.api}/validation/report/search`, { params });
  }

  // GET /aha/v1/validation/summary?session_id
  getValidationSummary(): Observable<ValidationSummary> {
    return this.http.get<ValidationSummary>(`${this.api}/validation/summary`, {
      params: { session_id: this.resolvedSessionId },
    });
  }

  notifyReplan(_failedVariables: string[]): Observable<void> {
    return this.http.post<void>(`${this.api}/validation/replan`, {
      session_id: this.resolvedSessionId,
    });
  }

  // GET /aha/v1/validation/pipeline-stats?session_id
  getPipelineStats(): Observable<PipelineStats> {
    return this.http.get<PipelineStats>(`${this.api}/validation/pipeline-stats`, {
      params: { session_id: this.resolvedSessionId },
    }).pipe(catchError(() => of({ dataset_name: '', modules: [] })));
  }

  // ── Images Module ─────────────────────────────────────────
  // GET  /aha/v1/images/folders?path
  // POST /aha/v1/images/start  →  stores session_id for this pipeline
  // GET  /aha/v1/images/status (SSE)
  // GET  /aha/v1/images/summary?session_id
  // GET  /aha/v1/images/gifs?session_id
  // GET  /aha/v1/images/gif?session_id&id  (binary — returned as URL)

  listImageFolders(path: string): Observable<ImageFolderList> {
    return this.http.get<ImageFolderList>(`${this.api}/images/folders`, {
      params: { path },
    });
  }

  runPreprocessing(folder: string): Observable<void> {
    return this.http.post<{ ok: boolean; session_id: string }>(
      `${this.api}/images/start`,
      { folder },
    ).pipe(
      tap(res => this.setSession(res.session_id)),
      map(() => undefined),
    );
  }

  watchPreprocessingStatus(_folder: string): Observable<StreamStatus> {
    return this.sseStream(`${this.api}/images/status?session_id=${this.resolvedSessionId}`);
  }

  getImageSummary(_folder: string): Observable<ImageSummaryData> {
    return this.http.get<ImageSummaryData>(`${this.api}/images/summary`, {
      params: { session_id: this.resolvedSessionId },
    });
  }

  listImageGifs(_folder: string, page: number, pageSize: number): Observable<ImageGifList> {
    return this.http.get<ImageGifList>(`${this.api}/images/gifs`, {
      params: { session_id: this.resolvedSessionId, page: String(page), page_size: String(pageSize) },
    });
  }

  getGifUrl(id: string): string {
    return `${this.api}/images/gif?session_id=${this.resolvedSessionId}&id=${encodeURIComponent(id)}`;
  }

  // ── Session history ───────────────────────────────────────

  getSessions(): Observable<Session[]> {
    return this.http.get<{ sessions: Session[] }>(`${this.api}/sessions`).pipe(
      map(res => res.sessions),
    );
  }

  renameSession(sessionId: string, name: string): Observable<{ ok: boolean }> {
    return this.http.patch<{ ok: boolean }>(`${this.api}/sessions/${encodeURIComponent(sessionId)}`, { name });
  }

  deleteSession(sessionId: string): Observable<void> {
    return this.http.delete<void>(`${this.api}/sessions/${encodeURIComponent(sessionId)}`);
  }

  finalizeSession(): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${this.api}/sessions/finalize`, { session_id: this.resolvedSessionId });
  }

  // POST /aha/v1/sessions/cancel
  // Same shape as finalize — the session id in the body, `{ ok: true }` back.
  cancelSession(): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${this.api}/sessions/cancel`, { session_id: this.resolvedSessionId });
  }
}
